import React, {FC, useCallback, useMemo, useRef} from 'react';
import {IProduct, IProductOption} from '../../../../../../types/galleryTypes';
import {ColorPicker} from 'wix-ui-tpa/cssVars';
import {useGlobals, withGlobals} from '../../../../../../globalPropsContext';
import {RadioButtonChangeEvent, RadioButtonClickEvent} from 'wix-ui-core';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import {useOptionSelectionLogic} from '../../../hooks/options/useOptionSelectionLogic';
import {useRadioButtonKeyboardNavigation} from '../hooks/useRadioButtonKeyboardNavigation';
import {classes as stylableColorOptionClasses} from './ProductQuickAddColorOption.st.css';

export enum ProductQuickAddColorOptionDataHook {
  Root = 'ProductQuickAddColorOptionDataHook.Root',
  ColorPickerElement = 'ProductQuickAddColorOptionDataHook.ColorPickerElement',
}

export interface IProductQuickAddColorOptionProps {
  productId: IProduct['id'];
  option: IProductOption;
}

const ProductQuickAddColorOptionComp: FC<IProductQuickAddColorOptionProps> = ({productId, option}) => {
  const {productsVariantInfoMap, handleProductsOptionsChange, styles, stylesParams} = useGlobals();
  const selectionIds = productsVariantInfoMap?.[productId]?.variantSelectionIds;
  const selectionsAvailability = productsVariantInfoMap?.[productId]?.selectionsAvailability[option.id];

  const {visibleSelections, calculateNextSelectionIds, isSelectionDisabled, isSelectionChecked} =
    useOptionSelectionLogic({
      option,
      selectionIds,
      selectionsAvailability,
    });

  const colorPickerContainerRef = useRef<HTMLDivElement>(null);

  const handleSelection = useCallback(
    (pickedSelectionId: number) => {
      void handleProductsOptionsChange({
        productId,
        selectionIds: calculateNextSelectionIds(pickedSelectionId),
        optionType: ProductOptionType.COLOR,
      });
    },
    [handleProductsOptionsChange, productId, calculateNextSelectionIds]
  );

  const {handleKeyboardSelection} = useRadioButtonKeyboardNavigation({
    containerRef: colorPickerContainerRef,
    visibleSelections,
    isSelectionDisabled,
    handleSelection,
  });

  const handleMouseSelection = useCallback(
    (event: RadioButtonChangeEvent | RadioButtonClickEvent) => {
      const pickedSelectionId = visibleSelections.find(({value}) => value === event.value)?.id;
      handleSelection(pickedSelectionId);
    },
    [handleSelection, visibleSelections]
  );

  const colorPickerItems = useMemo(() => {
    return visibleSelections.map((visibleSelection) => {
      const isDisabled = isSelectionDisabled(visibleSelection.id);

      return (
        <ColorPicker.Item
          key={visibleSelection.id}
          value={visibleSelection.value}
          id={visibleSelection.description}
          aria-label={visibleSelection.description}
          checked={isSelectionChecked(visibleSelection.id)}
          disabled={isDisabled}
          isCrossedOut={isDisabled}
        />
      );
    });
  }, [visibleSelections, isSelectionDisabled, isSelectionChecked]);

  const showLabel = styles.get(stylesParams.gallery_quickAddShowProductOptionsTitles);

  return (
    <div
      data-hook={ProductQuickAddColorOptionDataHook.Root}
      ref={colorPickerContainerRef}
      onKeyDown={handleKeyboardSelection}
      role="none">
      <ColorPicker
        className={stylableColorOptionClasses.colorOption}
        data-hook={ProductQuickAddColorOptionDataHook.ColorPickerElement}
        label={showLabel ? option.title : undefined}
        aria-label={option.title}
        name={option.id}
        required
        showAsterisk
        onChange={handleMouseSelection}>
        {colorPickerItems}
      </ColorPicker>
    </div>
  );
};

export const ProductQuickAddColorOption = withGlobals(ProductQuickAddColorOptionComp);
